import { useState, useEffect, useMemo, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import QuestionCard from './QuestionCard';
import { getPricingConfig, calculateFinalPrice } from '../../../services/pricingService';
import { resolveExperienceLevel } from '../../../utils/experienceLevel';

const DURATION_LABELS = {
  '1': 'ساعة',
  '2': 'ساعتان',
  '3': '٣ ساعات',
  '4': '٤ ساعات',
  '5': '٥ ساعات',
  '6': '٦ ساعات',
  '8': '٨ ساعات',
};
const DURATION_VALUES = ['1', '2', '3', '4', '5', '6', '8'];

const PHOTOGRAPHY_TYPE_LABELS = {
  'food':        'تصوير الطعام',
  'product':     'تصوير المنتجات',
  'event':       'تصوير الفعاليات',
  'real-estate': 'تصوير العقار',
  'wedding':     'تصوير حفل الزفاف',
  'portrait':    'التصوير الشخصي',
};

const buildDeliverableLabel = (photos, videos, shortVideos) => {
  const parts = [];
  if (photos > 0) parts.push(`${photos} صورة`);
  if (videos > 0) parts.push(`${videos} فيديو طويل`);
  if (shortVideos > 0) parts.push(`${shortVideos} فيديو قصير`);
  return parts.join(' + ') || '0 مخرجات';
};

const PACKAGES = [
  {
    id: 'elite',
    label: 'نخبة',
    subtext: 'خبرة واسعة — خدمة VIP',
    icon: 'Crown',
    gradientFrom: 'from-violet-600',
    gradientTo: 'to-purple-700',
    ringColor: 'ring-violet-300',
    borderSel: 'border-violet-400',
    accentBg: 'bg-violet-50',
    dotColor: 'bg-violet-500',
    defaultDuration: '3',
    defaultPhotos: 30,
    defaultVideos: 1,
    defaultShortVideos: 1,
  },
  {
    id: 'distinguished',
    label: 'مميز',
    subtext: 'خبرة متقدمة — معدات احترافية',
    icon: 'Star',
    gradientFrom: 'from-amber-400',
    gradientTo: 'to-orange-500',
    ringColor: 'ring-amber-300',
    borderSel: 'border-amber-400',
    accentBg: 'bg-amber-50',
    dotColor: 'bg-amber-500',
    recommended: true,
    defaultDuration: '2',
    defaultPhotos: 20,
    defaultVideos: 1,
    defaultShortVideos: 1,
  },
  {
    id: 'certified',
    label: 'معتمد',
    subtext: 'أسعار مناسبة — جودة مضمونة',
    icon: 'BadgeCheck',
    gradientFrom: 'from-emerald-500',
    gradientTo: 'to-green-600',
    ringColor: 'ring-emerald-300',
    borderSel: 'border-emerald-400',
    accentBg: 'bg-emerald-50',
    dotColor: 'bg-emerald-500',
    defaultDuration: '1',
    defaultPhotos: 10,
    defaultVideos: 1,
    defaultShortVideos: 1,
  },
];

/* ── قيم افتراضية للمخرجات بناءً على خيار حجم العمل ── */
const WORKLOAD_DEFAULTS = {
  // فعاليات
  'event_small':                { photos: 20, videos: 0, shortVideos: 1, duration: '1' },
  'event_medium':               { photos: 30, videos: 1, shortVideos: 1, duration: '2' },
  'event_large':                { photos: 50, videos: 1, shortVideos: 2, duration: '3' },
  // عقار
  'real-estate_small':          { photos: 20, videos: 0, shortVideos: 1, duration: '1' },
  'real-estate_medium':         { photos: 30, videos: 1, shortVideos: 2, duration: '2' },
  'real-estate_large':          { photos: 50, videos: 1, shortVideos: 3, duration: '3' },
  // طعام
  'food_small':                 { photos: 15, videos: 0, shortVideos: 1, duration: '1' },
  'food_medium':                { photos: 25, videos: 0, shortVideos: 2, duration: '2' },
  'food_large':                 { photos: 40, videos: 0, shortVideos: 2, duration: '3' },
  // منتجات
  'product_small':              { photos: 15, videos: 0, shortVideos: 1, duration: '1' },
  'product_medium':             { photos: 25, videos: 0, shortVideos: 1, duration: '2' },
  'product_large':              { photos: 35, videos: 0, shortVideos: 1, duration: '3' },
  'product_xlarge':             { photos: 50, videos: 0, shortVideos: 2, duration: '3' },
  // زفاف — مفاتيح فريدة لا تحتاج prefix
  'wedding_event_only':         { photos: 50,  videos: 1, shortVideos: 3, duration: '3' },
  'wedding_prep_and_event':     { photos: 70,  videos: 1, shortVideos: 5, duration: '5' },
  'wedding_full_coverage':      { photos: 100, videos: 1, shortVideos: 7, duration: '8' },
  // بورتريه — مفاتيح فريدة
  'portrait_one':               { photos: 5,  videos: 0, shortVideos: 0, duration: '1' },
  'portrait_two':               { photos: 10, videos: 0, shortVideos: 0, duration: '2' },
  'portrait_group':             { photos: 20, videos: 0, shortVideos: 0, duration: '2' },
};

const getWorkloadDefaults = (formData) => {
  const wb = formData?.workloadBucket;
  const pt = formData?.photographyType;
  if (!wb) return null;
  if (WORKLOAD_DEFAULTS[wb]) return WORKLOAD_DEFAULTS[wb];
  const key = pt ? `${pt}_${wb}` : null;
  return key ? (WORKLOAD_DEFAULTS[key] ?? null) : null;
};

/* ── حساب السعر لمجموعة قيم معينة ──
 * ملاحظة مهمة: نُثبّت equipment على 'basic-editing' (التعديل الأساسي المجاني) دائماً هنا،
 * بغض النظر عمّا هو مخزّن حالياً في formData.equipment. السبب: إذا اختار العميل لاحقاً
 * "تعديل احترافي" في خطوة التعديل ثم رجع لهذه الخطوة (خطوة الباقات) لتعديل الساعات/المخرجات،
 * فإن ...formData كانت تحمل equipment القديم فيُحسب سعر الباقة هنا وهو يتضمّن تكلفة
 * التعديل الاحترافي خطأً، مما يجعل المبلغ الظاهر في هذه الخطوة يتذبذب حسب تنقّل العميل.
 * التعديل الاحترافي يجب أن يُضاف فقط في خطوته المخصّصة (EquipmentStep) وليس قبلها. */
const calcPrice = (pricingConfig, formData, expId, dur, photos, videos, shortVideos) => {
  if (!pricingConfig) return null;
  try {
    const draft = {
      ...formData,
      experienceLevel: expId,
      duration: dur,
      deliverableMode: 'custom',
      photosCount: photos,
      videosCount: videos,
      shortVideosCount: shortVideos,
      equipment: ['basic-editing'],
    };
    return calculateFinalPrice(draft, pricingConfig).total;
  } catch {
    return null;
  }
};

/* ── زر التعديل ── */
const AdjBtn = ({ onClick, disabled, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-9 h-9 rounded-xl bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-35 disabled:cursor-not-allowed flex items-center justify-center transition-all active:scale-90 shadow-md"
  >
    {children}
  </button>
);

/* ── صف مخرجات واحد ── */
const DeliverableRow = ({ iconName, iconClass, label, value, onDec, onInc, active, step = 1 }) => (
  <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/70 border border-slate-100">
    <div className="flex items-center gap-2">
      <Icon name={iconName} size={15} className={iconClass} />
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <AdjBtn onClick={onDec} disabled={!active || value <= 0}>
        <Icon name="Minus" size={13} className="text-slate-600" />
      </AdjBtn>
      <span className="w-6 text-center text-sm font-bold text-slate-800 tabular-nums">{value}</span>
      <AdjBtn onClick={onInc} disabled={!active}>
        <Icon name="Plus" size={13} className="text-slate-600" />
      </AdjBtn>
    </div>
  </div>
);

/* ════════════════════════════════ COMPONENT ════════════════════════════════ */
const PackagesStep = ({ formData, onUpdate, isDirectMode = false, preselectedPhotographer = null }) => {

  /* ── مستوى الخبرة في الحجز المباشر مقفّل من ملف المصور ── */
  const directExpResolved = resolveExperienceLevel(
    preselectedPhotographer?.experienceLevel || preselectedPhotographer?.experience_level,
    'distinguished'
  );
  const directExpId = directExpResolved.ui;
  const directPkg = PACKAGES.find(p => p.id === directExpId) || PACKAGES[1];
  const photographerName = preselectedPhotographer?.name || 'المصور';

  /* ── State ── */
  const [selectedId, setSelectedId] = useState(
    isDirectMode ? directExpId : (formData?.experienceLevel || '')
  );
  const [duration, setDuration] = useState(() => {
    if (formData?.duration) return formData.duration;
    const wd = getWorkloadDefaults(formData);
    if (wd) return wd.duration;
    return isDirectMode ? directPkg.defaultDuration : '';
  });
  const [photosCount, setPhotosCount] = useState(() => {
    if (formData?.photosCount != null) return formData.photosCount;
    const wd = getWorkloadDefaults(formData);
    if (wd) return wd.photos;
    return isDirectMode ? directPkg.defaultPhotos : 10;
  });
  const [videosCount, setVideosCount] = useState(() => {
    if (formData?.videosCount != null) return formData.videosCount;
    const wd = getWorkloadDefaults(formData);
    if (wd) return wd.videos;
    return isDirectMode ? directPkg.defaultVideos : 0;
  });
  const [shortVideosCount, setShortVideosCount] = useState(() => {
    if (formData?.shortVideosCount != null) return formData.shortVideosCount;
    const wd = getWorkloadDefaults(formData);
    if (wd) return wd.shortVideos;
    return isDirectMode ? directPkg.defaultShortVideos : 0;
  });
  const [pricingConfig, setPricingConfig] = useState(null);
  const scrollContainerRef = useRef(null);
  const cardRefs = useRef([]);
  const scrollEndTimerRef = useRef(null);

  /* جلب إعدادات التسعير مرة واحدة (مخزنة مؤقتاً في الـ service) */
  useEffect(() => {
    getPricingConfig().then(setPricingConfig).catch(() => {});
  }, []);

  /* في الحجز المباشر: أرسل البيانات الأولية فور تحميل الخطوة */
  useEffect(() => {
    if (!isDirectMode) return;
    if (formData?.duration && formData?.photosCount != null) return; // مُهيَّأ مسبقاً
    const wd = getWorkloadDefaults(formData);
    const dur       = wd ? wd.duration    : directPkg.defaultDuration;
    const photos    = wd ? wd.photos      : directPkg.defaultPhotos;
    const videos    = wd ? wd.videos      : directPkg.defaultVideos;
    const shortVids = wd ? wd.shortVideos : directPkg.defaultShortVideos;
    onUpdate({
      experienceLevel: directExpId,
      experienceLevelLabel: directExpResolved.labelAr,
      duration: dur,
      durationLabel: DURATION_LABELS[dur] || `${dur} ساعة`,
      deliverableMode: 'custom',
      photosCount: photos,
      videosCount: videos,
      shortVideosCount: shortVids,
      deliverableLabel: buildDeliverableLabel(photos, videos, shortVids),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectMode]);

  /* أسعار البطاقات الافتراضية — تُحسب عند توفر الـ config أو تغيير حجم العمل */
  const defaultPrices = useMemo(() => {
    if (!pricingConfig) return {};
    const wd = getWorkloadDefaults(formData);
    const result = {};
    PACKAGES.forEach(pkg => {
      const dur       = wd ? wd.duration    : pkg.defaultDuration;
      const photos    = wd ? wd.photos      : pkg.defaultPhotos;
      const videos    = wd ? wd.videos      : pkg.defaultVideos;
      const shortVids = wd ? wd.shortVideos : pkg.defaultShortVideos;
      result[pkg.id] = calcPrice(pricingConfig, formData, pkg.id, dur, photos, videos, shortVids);
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingConfig, formData?.workloadBucket, formData?.photographyType]);

  /* سعر البطاقة المختارة — يتحدث فورياً مع كل ضغطة +/− */
  const selectedPrice = useMemo(() => {
    if (!pricingConfig || !selectedId || !duration) return null;
    return calcPrice(pricingConfig, formData, selectedId, duration, photosCount, videosCount, shortVideosCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingConfig, selectedId, duration, photosCount, videosCount, shortVideosCount]);

  const pushUpdate = (expId, expLabel, dur, photos, videos, shortVideos) => {
    onUpdate({
      experienceLevel: expId,
      experienceLevelLabel: expLabel,
      duration: dur,
      durationLabel: DURATION_LABELS[dur] || `${dur} ساعة`,
      deliverableMode: 'custom',
      photosCount: photos,
      videosCount: videos,
      shortVideosCount: shortVideos,
      deliverableLabel: buildDeliverableLabel(photos, videos, shortVideos),
    });
  };

  const handleSelectPackage = (pkg) => {
    const wd = getWorkloadDefaults(formData);
    const dur       = wd ? wd.duration    : pkg.defaultDuration;
    const photos    = wd ? wd.photos      : pkg.defaultPhotos;
    const videos    = wd ? wd.videos      : pkg.defaultVideos;
    const shortVids = wd ? wd.shortVideos : pkg.defaultShortVideos;
    setSelectedId(pkg.id);
    setDuration(dur);
    setPhotosCount(photos);
    setVideosCount(videos);
    setShortVideosCount(shortVids);
    pushUpdate(pkg.id, pkg.label, dur, photos, videos, shortVids);
  };

  const adjustDuration = (e, delta) => {
    e.stopPropagation();
    const expId = isDirectMode ? directExpId : selectedId;
    const expLabel = isDirectMode ? directPkg.label : (PACKAGES.find(p => p.id === selectedId)?.label || selectedId);
    const idx = DURATION_VALUES.indexOf(duration);
    const newIdx = Math.max(0, Math.min(DURATION_VALUES.length - 1, idx + delta));
    const newDur = DURATION_VALUES[newIdx];
    setDuration(newDur);
    pushUpdate(expId, expLabel, newDur, photosCount, videosCount, shortVideosCount);
  };

  const adjustField = (e, field, delta) => {
    e.stopPropagation();
    const expId = isDirectMode ? directExpId : selectedId;
    const expLabel = isDirectMode ? directPkg.label : (PACKAGES.find(p => p.id === selectedId)?.label || selectedId);
    let p = photosCount, v = videosCount, s = shortVideosCount;
    if (field === 'photos')      p = Math.max(0, p + delta);
    if (field === 'videos')      v = Math.max(0, v + delta);
    if (field === 'shortVideos') s = Math.max(0, s + delta);
    setPhotosCount(p); setVideosCount(v); setShortVideosCount(s);
    pushUpdate(expId, expLabel, duration, p, v, s);
  };

  const selectedPkg = PACKAGES.find(p => p.id === selectedId);

  /* ── Auto-select on scroll snap (regular mode, mobile only) ── */
  useEffect(() => {
    if (isDirectMode) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const isMobile = () => window.innerWidth < 768;

    const selectSnappedCard = () => {
      if (!isMobile()) return;
      const containerRect = container.getBoundingClientRect();
      const containerCenterX = containerRect.left + containerRect.width / 2;
      let closestIdx = -1;
      let closestDist = Infinity;
      cardRefs.current.forEach((card, idx) => {
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const dist = Math.abs(cardCenterX - containerCenterX);
        if (dist < closestDist) { closestDist = dist; closestIdx = idx; }
      });
      if (closestIdx >= 0) {
        const pkg = PACKAGES[closestIdx];
        setSelectedId(prev => {
          if (prev === pkg.id) return prev;
          const wd = getWorkloadDefaults(formData);
          const dur       = wd ? wd.duration    : pkg.defaultDuration;
          const photos    = wd ? wd.photos      : pkg.defaultPhotos;
          const videos    = wd ? wd.videos      : pkg.defaultVideos;
          const shortVids = wd ? wd.shortVideos : pkg.defaultShortVideos;
          setDuration(dur);
          setPhotosCount(photos);
          setVideosCount(videos);
          setShortVideosCount(shortVids);
          setTimeout(() => {
            onUpdate({
              experienceLevel: pkg.id,
              experienceLevelLabel: pkg.label,
              duration: dur,
              durationLabel: DURATION_LABELS[dur] || `${dur} ساعة`,
              deliverableMode: 'custom',
              photosCount: photos,
              videosCount: videos,
              shortVideosCount: shortVids,
              deliverableLabel: buildDeliverableLabel(photos, videos, shortVids),
            });
          }, 0);
          return pkg.id;
        });
      }
    };

    const onScrollEnd = () => selectSnappedCard();
    const onScroll = () => {
      clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(selectSnappedCard, 150);
    };
    const supportsScrollEnd = 'onscrollend' in window;
    if (supportsScrollEnd) {
      container.addEventListener('scrollend', onScrollEnd, { passive: true });
    } else {
      container.addEventListener('scroll', onScroll, { passive: true });
    }
    return () => {
      clearTimeout(scrollEndTimerRef.current);
      if (supportsScrollEnd) container.removeEventListener('scrollend', onScrollEnd);
      else container.removeEventListener('scroll', onScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectMode, onUpdate]);

  /* ════ DIRECT MODE — بطاقة واحدة مركزية ════ */
  if (isDirectMode) {
    const pkg = directPkg;
    return (
      <QuestionCard
        icon="Layers"
        title={`تفاصيل مقترحة لـ ${PHOTOGRAPHY_TYPE_LABELS[formData?.photographyType] || 'جلستك'}`}
        description="شائعة لهذا النوع — عدّل ما يناسبك"
      >
        <div className="flex justify-center">
          <div className={`w-full max-w-[320px] rounded-2xl border-2 overflow-hidden shadow-xl ${pkg.borderSel} ring-2 ${pkg.ringColor}`}>

            {/* ── Gradient header ── */}
            <div className={`bg-gradient-to-br ${pkg.gradientFrom} ${pkg.gradientTo} p-4 pb-5`}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon
                    name={pkg.icon}
                    size={20}
                    color="white"
                    fill={pkg.id === 'distinguished' ? 'white' : 'none'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-lg leading-tight">
                    مستوى {pkg.label}
                  </div>
                  <div className="text-white/70 text-xs mt-0.5 leading-tight">{pkg.subtext}</div>
                </div>
                <div className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                  <Icon name="Check" size={13} color="white" />
                </div>
              </div>
            </div>

            {/* ── Card body ── */}
            <div className={`p-3.5 space-y-3 ${pkg.accentBg}`}>

              {/* Duration */}
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${pkg.dotColor}`} />
                  <span className="text-xs font-semibold text-slate-500 tracking-wide">مدة الجلسة</span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/80 border border-slate-100">
                  <AdjBtn onClick={(e) => adjustDuration(e, -1)} disabled={DURATION_VALUES.indexOf(duration) === 0}>
                    <Icon name="Minus" size={13} className="text-slate-600" />
                  </AdjBtn>
                  <span className="text-base font-bold text-slate-800">
                    {DURATION_LABELS[duration] || `${duration} ساعة`}
                  </span>
                  <AdjBtn onClick={(e) => adjustDuration(e, 1)} disabled={DURATION_VALUES.indexOf(duration) === DURATION_VALUES.length - 1}>
                    <Icon name="Plus" size={13} className="text-slate-600" />
                  </AdjBtn>
                </div>
              </div>

              {/* Deliverables */}
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${pkg.dotColor}`} />
                  <span className="text-xs font-semibold text-slate-500 tracking-wide">عدد المخرجات</span>
                </div>
                <div className="space-y-1.5">
                  <DeliverableRow iconName="Image" iconClass="text-blue-500" label="صور"
                    value={photosCount} active
                    onDec={(e) => adjustField(e, 'photos', -5)}
                    onInc={(e) => adjustField(e, 'photos', 5)}
                  />
                  <DeliverableRow iconName="Video" iconClass="text-blue-500" label="فيديو طويل"
                    value={videosCount} active
                    onDec={(e) => adjustField(e, 'videos', -1)}
                    onInc={(e) => adjustField(e, 'videos', 1)}
                  />
                  <DeliverableRow iconName="Clapperboard" iconClass="text-purple-500" label="فيديو قصير"
                    value={shortVideosCount} active
                    onDec={(e) => adjustField(e, 'shortVideos', -1)}
                    onInc={(e) => adjustField(e, 'shortVideos', 1)}
                  />
                </div>
              </div>

              {/* Price footer */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100/80">
                <span className="text-[11px] text-slate-400">السعر الإجمالي</span>
                {selectedPrice != null ? (
                  <span className="text-2xl font-extrabold tracking-tight text-primary">
                    {selectedPrice.toLocaleString('en-US')}
                    <span className="text-xs font-semibold text-slate-400 mr-1">ر.س</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-slate-400">
                    <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin inline-block" />
                    <span className="text-xs">جاري الحساب</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary strip */}
        <div className="mt-3 px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl flex items-center gap-3">
          <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0" />
          <p className="text-sm text-slate-700 leading-snug">
            {DURATION_LABELS[duration] || `${duration} ساعة`}
            {photosCount > 0 && ` · ${photosCount} صورة`}
            {videosCount > 0 && ` · ${videosCount} فيديو طويل`}
            {shortVideosCount > 0 && ` · ${shortVideosCount} فيديو قصير`}
          </p>
        </div>
      </QuestionCard>
    );
  }

  /* ════ REGULAR MODE — ثلاث بطاقات قابلة للسحب ════ */
  return (
    <QuestionCard
      icon="Layers"
      title={`تفاصيل مقترحة لـ ${PHOTOGRAPHY_TYPE_LABELS[formData?.photographyType] || 'جلستك'}`}
      description="باقات شائعة حسب مستوى المصور - يمكنك تخصيص باقتك حسب احتياجك"
    >
      {/* Hint strip (mobile only) */}
      <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3 md:hidden">
        <Icon name="ChevronLeft" size={13} className="text-slate-300" />
        <span>اسحب لاستعراض الباقات</span>
        <Icon name="ChevronRight" size={13} className="text-slate-300" />
      </div>

      {/* ── Horizontal scroll track ── */}
      <div
        ref={scrollContainerRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1 scrollbar-hide"
      >
        {PACKAGES.map((pkg, pkgIdx) => {
          const isSelected = selectedId === pkg.id;
          const wd = getWorkloadDefaults(formData);
          const shownDuration = isSelected ? duration        : (wd ? wd.duration    : pkg.defaultDuration);
          const shownPhotos   = isSelected ? photosCount     : (wd ? wd.photos      : pkg.defaultPhotos);
          const shownVideos   = isSelected ? videosCount     : (wd ? wd.videos      : pkg.defaultVideos);
          const shownShort    = isSelected ? shortVideosCount : (wd ? wd.shortVideos : pkg.defaultShortVideos);

          return (
            <div
              key={pkg.id}
              ref={el => { cardRefs.current[pkgIdx] = el; }}
              onClick={() => handleSelectPackage(pkg)}
              className={`
                relative flex-shrink-0 w-[268px] snap-start rounded-2xl border-2 transition-all duration-200 cursor-pointer overflow-hidden
                ${isSelected
                  ? `${pkg.borderSel} ring-2 ${pkg.ringColor} shadow-xl`
                  : 'border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'}
              `}
            >
              {pkg.recommended && (
                <div className="absolute top-2 left-3 z-10">
                  <span className="px-2.5 py-0.5 text-[10px] font-bold bg-white/20 backdrop-blur-sm text-white rounded-full border border-white/30">
                    ⭐ موصى به
                  </span>
                </div>
              )}

              {/* Header */}
              <div className={`bg-gradient-to-br ${pkg.gradientFrom} ${pkg.gradientTo} p-4 pb-5`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Icon name={pkg.icon} size={20} color="white" fill={pkg.id === 'distinguished' ? 'white' : 'none'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold text-lg leading-tight">مستوى {pkg.label}</div>
                    <div className="text-white/70 text-xs mt-0.5 leading-tight">{pkg.subtext}</div>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={13} color="white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className={`p-3.5 space-y-3 ${isSelected ? pkg.accentBg : 'bg-white'} transition-colors duration-200`}>

                {/* Duration */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${pkg.dotColor}`} />
                    <span className="text-xs font-semibold text-slate-500 tracking-wide">مدة الجلسة</span>
                  </div>
                  <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-white/80 border border-slate-100">
                    <AdjBtn onClick={(e) => adjustDuration(e, -1)} disabled={!isSelected || DURATION_VALUES.indexOf(shownDuration) === 0}>
                      <Icon name="Minus" size={13} className="text-slate-600" />
                    </AdjBtn>
                    <span className="text-base font-bold text-slate-800">
                      {DURATION_LABELS[shownDuration] || `${shownDuration} ساعة`}
                    </span>
                    <AdjBtn onClick={(e) => adjustDuration(e, 1)} disabled={!isSelected || DURATION_VALUES.indexOf(shownDuration) === DURATION_VALUES.length - 1}>
                      <Icon name="Plus" size={13} className="text-slate-600" />
                    </AdjBtn>
                  </div>
                </div>

                {/* Deliverables */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${pkg.dotColor}`} />
                    <span className="text-xs font-semibold text-slate-500 tracking-wide">عدد المخرجات</span>
                  </div>
                  <div className="space-y-1.5">
                    <DeliverableRow iconName="Image" iconClass="text-blue-500" label="صور"
                      value={shownPhotos} active={isSelected} step={5}
                      onDec={(e) => adjustField(e, 'photos', -5)} onInc={(e) => adjustField(e, 'photos', 5)}
                    />
                    <DeliverableRow iconName="Video" iconClass="text-blue-500" label="فيديو طويل"
                      value={shownVideos} active={isSelected}
                      onDec={(e) => adjustField(e, 'videos', -1)} onInc={(e) => adjustField(e, 'videos', 1)}
                    />
                    <DeliverableRow iconName="Clapperboard" iconClass="text-purple-500" label="فيديو قصير"
                      value={shownShort} active={isSelected}
                      onDec={(e) => adjustField(e, 'shortVideos', -1)} onInc={(e) => adjustField(e, 'shortVideos', 1)}
                    />
                  </div>
                </div>

                {/* Price footer */}
                {(() => {
                  const displayPrice = isSelected ? selectedPrice : defaultPrices[pkg.id];
                  return (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100/80">
                      <span className="text-[11px] text-slate-400">
                        {isSelected ? 'السعر الإجمالي' : 'ابتداءً من'}
                      </span>
                      {displayPrice != null ? (
                        <span className={`text-2xl font-extrabold tracking-tight ${isSelected ? 'text-primary' : 'text-slate-700'}`}>
                          {displayPrice.toLocaleString('en-US')}
                          <span className="text-xs font-semibold text-slate-400 mr-1">ر.س</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-slate-400">
                          <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin inline-block" />
                          <span className="text-xs">جاري الحساب</span>
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selection summary */}
      {selectedPkg ? (
        <div className="mt-1 px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl flex items-center gap-3">
          <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0" />
          <p className="text-sm text-slate-700 leading-snug">
            <strong>باقة {selectedPkg.label}</strong>
            {' · '}
            {DURATION_LABELS[duration] || `${duration} ساعة`}
            {photosCount > 0 && ` · ${photosCount} صورة`}
            {videosCount > 0 && ` · ${videosCount} فيديو طويل`}
            {shortVideosCount > 0 && ` · ${shortVideosCount} فيديو قصير`}
          </p>
        </div>
      ) : (
        <div className="mt-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
          <Icon name="Info" size={16} className="text-slate-400 flex-shrink-0" />
          <p className="text-sm text-slate-500">اضغط على الباقة المناسبة لاختيارها</p>
        </div>
      )}
    </QuestionCard>
  );
};

export default PackagesStep;
